/**
 * Hooks do Início do portal do parceiro (PR 5) — feed da Central em rolagem
 * infinita e leituras obrigatórias pendentes. O post destacado (`?post=`,
 * `usePortalCentralPost`) e a mutation base de confirmar leitura
 * (`useConfirmRead`) já existem em `usePortalCentralFeed.ts` (PR 4 /
 * E21.5.2) — aqui só o que é NOVO desta tela.
 */
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";

import { useConfirmRead } from "@/hooks/usePortalCentralFeed";
import { portalCentralApi, type PortalFeedResponse } from "@/services/portalApi";

/**
 * Prefixo de propósito igual ao de `usePortalCentralFeed` (`["portal",
 * "central", "feed", page, pageSize]`): React Query casa `invalidateQueries`
 * por PREFIXO (`exact: false` é o padrão), então confirmar uma leitura em
 * QUALQUER lugar do portal (Central de hoje ou este feed infinito do Início)
 * invalida os dois, sem precisar tocar em `usePortalCentralFeed.ts`.
 */
const FEED_INFINITO_KEY = ["portal", "central", "feed", "infinito"] as const;
const PENDING_READS_KEY = ["portal", "central", "pending-reads"] as const;

/**
 * Feed da Central em rolagem infinita (Início) — mesmo endpoint de
 * `usePortalCentralFeed`, só que acumulando páginas em vez de trocar. Próxima
 * página enquanto `page * page_size < total` (contrato combinado no brief da
 * task, não `items.length < page_size`: a última página pode vir cheia).
 *
 * I2 (revisão final) — `habilitado` (default `true`) é `temComunicados` (a
 * permissão `portal:central:view`) vindo da página. Antes, só o RENDER era
 * gateado (`comunicadosDoSlot` devolvia `null`): a query rodava do mesmo
 * jeito e o parceiro sem a Central levava 403 do backend a cada visita (2
 * chamadas — feed + pendências — por causa do `retry: 1` do app), com o
 * console cheio de erro vermelho numa tela que nem mostrava a seção.
 */
export function usePortalComunicados(pageSize = 10, habilitado = true) {
  return useInfiniteQuery({
    queryKey: [...FEED_INFINITO_KEY, pageSize],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      portalCentralApi.getFeed({ page: pageParam, page_size: pageSize }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: PortalFeedResponse) =>
      lastPage.page * lastPage.page_size < lastPage.total ? lastPage.page + 1 : undefined,
    staleTime: 30000,
    enabled: habilitado,
  });
}

/**
 * Leituras obrigatórias pendentes do parceiro — a quantidade do aviso âmbar
 * do Início. I2: mesmo `habilitado` de `usePortalComunicados`, mesma razão —
 * `/central/pending-reads` também mapeia pra tela `central` no backend.
 */
export function usePortalLeiturasPendentes(habilitado = true) {
  return useQuery({
    queryKey: PENDING_READS_KEY,
    queryFn: () => portalCentralApi.getPendingReads(),
    staleTime: 30000,
    enabled: habilitado,
  });
}

/**
 * Confirmar leitura a partir do Início: reusa a MESMA mutation de
 * `useConfirmRead` (já invalida o feed paginado + o post destacado — ver
 * comentário em `usePortalCentralFeed.ts`) e AINDA invalida a lista de
 * pendências, que só passou a existir no frontend com esta tela (por isso não
 * está no `onSuccess` de `useConfirmRead`: aquele hook nasceu antes dela
 * existir, e continua servindo a Central de hoje, que não mostra o aviso).
 */
export function useConfirmarLeituraDoInicio() {
  const qc = useQueryClient();
  const confirm = useConfirmRead();

  return {
    confirmar: (postId: string) =>
      confirm.mutate(postId, {
        onSuccess: () => qc.invalidateQueries({ queryKey: PENDING_READS_KEY }),
      }),
    isPending: confirm.isPending,
    variables: confirm.variables,
  };
}
